import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import React from "react";

import { BodyM, BodyS } from "@brexhq/shared/components/Typography/Components";

import { CellContent } from "./ComparisonTableCell";
import { Row } from "./ComparisonTableRow";

interface InlineTableRow {
  _key: string;
  cells: string[];
  _type: string;
}

interface InlineTableProps {
  value: {
    _key: string;
    rows: InlineTableRow[];
    title: string;
    columnHeaders: string[];
    _type: string;
  };
}

const Container = styled.div({
  width: "100%",
  height: "100%",
  margin: 0,
  marginBottom: "32px",
});

const Table = styled.table(({ theme }) => ({
  width: "100%",
  height: "100%",
  margin: 0,
  borderCollapse: "collapse",
  tableLayout: "fixed",
  borderLeft: `1px solid ${theme.colors.border.default}`,
  borderRight: `1px solid ${theme.colors.border.default}`,
}));

const THead = styled.thead(({ theme }) => ({
  [theme.breakpoints.SML]: {
    position: "sticky",
    top: theme.navHeights.mobile,
    zIndex: 1,
  },
}));

const HeaderCell = styled.th(({ theme }) => ({
  padding: 0,
  margin: 0,
  height: "32px",
  fontWeight: 600,
  borderTop: `1px solid ${theme.colors.border.default}`,
  borderRight: `1px solid ${theme.colors.border.default}`,
  "&:last-child": {
    borderRight: "none",
  },
  [theme.breakpoints.SML]: {
    height: "auto",
  },
}));

const DataCell = styled.td(({ theme }) => ({
  padding: 0,
  margin: 0,
  height: "32px",
  borderRight: `1px solid ${theme.colors.border.default}`,
  "&:last-child": {
    borderRight: "none",
  },
  [theme.breakpoints.SML]: {
    height: "auto",
  },
}));

const HeaderText = styled(BodyM)(({ theme }) => ({
  color: theme.colors.content.primary,
  fontVariationSettings: `${theme.fonts.FontWeight.MEDIUM} !important`,
}));

const CellText = styled(BodyM)(({ theme }) => ({
  color: theme.colors.content.primary,
}));

const Caption = styled.span(({ theme }) => ({
  display: "block",
  textAlign: "center",
  width: "100%",
  color: `${theme.colors.content.tertiary}`,
  fontStyle: "italic",
  opacity: 0.5,
  padding: "16px 0",
}));

export const InlineTable = ({ value }: InlineTableProps) => {
  const theme = useTheme();

  return (
    <Container>
      <Table theme={theme}>
        {value.columnHeaders ? (
          <THead>
            <Row backgroundColor={theme.colors.background.secondary}>
              {value.columnHeaders.map((header) => (
                <HeaderCell key={header}>
                  <CellContent borders={theme.colors.border.default}>
                    <HeaderText as="h4" theme={theme}>
                      {header}
                    </HeaderText>
                  </CellContent>
                </HeaderCell>
              ))}
            </Row>
          </THead>
        ) : null}
        <tbody>
          {value.rows.map((row) => (
            <Row
              key={row._key}
              backgroundColor={theme.colors.background.primary}
            >
              {row.cells.map((cell) => (
                <DataCell key={`${row._key}`}>
                  <CellContent borders={theme.colors.border.default}>
                    <CellText as="p" color={theme.colors.content.primary}>
                      {cell}
                    </CellText>
                  </CellContent>
                </DataCell>
              ))}
            </Row>
          ))}
        </tbody>
      </Table>
      {value.title && (
        <Caption>
          <BodyS>{value.title}</BodyS>
        </Caption>
      )}
    </Container>
  );
};
